import { CreditCardIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from './result.const';
import { ResultLayout, SuccessIllustration } from './ResultLayout';
import { useCountdownEnd } from './useCountdownEnd';

interface RegistrationSuccessProps {
  countdownSeconds?: number;
  onAddNow: () => void;
  onLater: () => void;
  onCountdownEnd?: () => void;
}

export function RegistrationSuccess({
  countdownSeconds,
  onAddNow,
  onLater,
  onCountdownEnd
}: Readonly<RegistrationSuccessProps>) {
  const [stopped, setStopped] = useState(false);
  const counting = countdownSeconds !== undefined && !stopped;
  const remaining = useCountdownEnd(
    countdownSeconds ?? 0,
    counting,
    onCountdownEnd
  );

  // Any touch or key press means the user is still here, so the automatic return is cancelled.
  useEffect(() => {
    if (!counting) return;
    const stop = () => setStopped(true);
    document.addEventListener('pointerdown', stop);
    document.addEventListener('keydown', stop);
    return () => {
      document.removeEventListener('pointerdown', stop);
      document.removeEventListener('keydown', stop);
    };
  }, [counting]);

  return (
    <ResultLayout>
      <SuccessIllustration alt="Registration successful" />

      <h1 className="mt-6 text-center text-2xl font-bold text-gray-900">
        Registration successful!
      </h1>
      <p className="mt-2 text-center text-sm text-gray-500">
        You can now checkout with your face at any store accepting AyeFace.
      </p>

      <button
        type="button"
        onClick={onAddNow}
        className={`mt-8 flex items-center justify-center gap-2 ${PRIMARY_BUTTON}`}
      >
        <CreditCardIcon className="h-5 w-5" aria-hidden="true" />
        Add a payment method now
      </button>
      <button
        type="button"
        onClick={onLater}
        className={`mt-3 ${SECONDARY_BUTTON}`}
      >
        Maybe later
      </button>

      <p className="mt-4 text-center text-xs text-gray-500">
        You can add a payment method anytime via user portal{' '}
        <a
          href="https://user.ayeface.com"
          className="font-semibold text-[#5CB891] underline"
        >
          user.ayeface.com
        </a>
      </p>

      {counting && (
        <p className="mt-6 text-center text-sm text-gray-500">
          Back to checkout in {remaining}s
        </p>
      )}
    </ResultLayout>
  );
}
